import { type TUserManagementRes } from "@/components/data/userManagementForm";
import axiosInstance from "@/lib/axiosInstance";
import { buildFilterByParam } from "@/utils/buildFilterByParam";
import { getQueryParamsWithCustomQueryObj } from "@/utils/getQueryParamsFromCustomQueryObject";
import { userManagementEndpoints } from "./userManagement.endpoints";
import type {
  ExclusionTypesAPIResponse,
  GetExclusionOptionsParams,
  GetReportsToParams,
  GetRoleOptionsParams,
  GetUserManagementDataParams,
  GetUserByIdResponse,
  UserManagementAPIResponse,
  UserDirectoryResponse,
} from "./userManagement.queries.types";
import type { UsersDropdownResponse } from "./userManagement.mutations.types";
import type { CustomAxiosRequestConfig } from "@/types/common.types";

export const UserManagementQueryKeys = {
  roles: "roles",
  userManagementForm: "activeData",
  reportTo: "reportTo",
  superAdminReportTo: "superAdminReportTo",
  roleOptions: "roleOptions",
  exclusions: "exclusions",
  exclusionTypes: "exclusionTypes",
  userDetail: "userDetail",
  userPickerOptions: "userPickerOptions",
} as const;

export const getUserManagementData = async ({
  search,
  sortBy,
  sortOrder,
  page,
  pageSize,
  filters,
  signal,
}: GetUserManagementDataParams = {}): Promise<TUserManagementRes> => {
  const params = new URLSearchParams();

  // Sent with every request, filters included: without them the backend falls back to page 1 of
  // ten rows, so a filtered list could not be paged and an export would stop at ten.
  // `GetUsersQuerySchema` accepts `page`, `pageSize` and `filterBy` together.
  params.append("page", page?.toString() ?? "1");
  params.append("pageSize", pageSize?.toString() ?? "10");

  if (search) {
    params.append("search", search);
  }

  if (sortBy) {
    params.append("sortBy", sortBy);
  }

  if (sortOrder) {
    params.append("sortOrder", sortOrder);
  }

  const filterBy = buildFilterByParam(filters, {
    keyMap: { user: "username" },
    // These keys take ids; the rest text-match a displayed name. `tenantId` is the entity filter
    // of the ServEngine-level list, which the backend reads as entity uuids.
    getValue: (f) =>
      f.filterBy === "reportsTo" || f.filterBy === "assignedTo" || f.filterBy === "tenantId"
        ? f.filterValue.id
        : f.filterValue.value,
  });
  if (filterBy) {
    params.append("filterBy", filterBy);
  }

  const { data } = await axiosInstance.get(
    `${userManagementEndpoints.users}?${getQueryParamsWithCustomQueryObj(params)}`,
    { signal },
  );
  return data;
};

export const getReportsTo = async ({
  page = 1,
  pageSize = 500,
  signal,
  excludeUserId,
  actingEntityId,
}: GetReportsToParams = {}): Promise<UserManagementAPIResponse> => {
  const queryParams: Record<string, string> = {};

  if (page != null) {
    queryParams.page = page.toString();
  }
  if (pageSize != null) {
    queryParams.pageSize = pageSize.toString();
  }
  if (excludeUserId) {
    queryParams.excludeUserId = excludeUserId;
  }
  // ReportsTo dropdown does not support search param - do not send it.

  const { data } = await axiosInstance.get(
    `${userManagementEndpoints.reportsTo}&${getQueryParamsWithCustomQueryObj(queryParams)}`,
    { signal, ...(actingEntityId ? { actingEntityId } : {}) } as CustomAxiosRequestConfig,
  );
  return data;
};

export const getSuperAdminReportsTo = async ({
  page = 1,
  pageSize = 500,
  signal,
  excludeUserId,
}: GetReportsToParams = {}): Promise<UserManagementAPIResponse> => {
  const queryParams: Record<string, string> = {};

  if (page != null) {
    queryParams.page = page.toString();
  }
  if (pageSize != null) {
    queryParams.pageSize = pageSize.toString();
  }
  if (excludeUserId) {
    queryParams.excludeUserId = excludeUserId;
  }

  const { data } = await axiosInstance.get(
    `${userManagementEndpoints.superAdminReportsTo}&${getQueryParamsWithCustomQueryObj(queryParams)}`,
    { signal },
  );
  return data;
};

export const getRoleOptions = async ({
  page = 1,
  pageSize = 500,
  search = "",
  signal,
  tenantId,
}: GetRoleOptionsParams = {}): Promise<UserManagementAPIResponse> => {
  const queryParams: Record<string, string> = {};

  if (page != null) {
    queryParams.page = page.toString();
  }
  if (pageSize != null) {
    queryParams.pageSize = pageSize.toString();
  }
  if (search) {
    queryParams.search = search;
  }

  if (tenantId) {
    queryParams.filterBy = "tenantId";
    queryParams.filterValue = tenantId;
  }

  const { data } = await axiosInstance.get(
    `${userManagementEndpoints.roles}&${getQueryParamsWithCustomQueryObj(queryParams)}`,
    { signal },
  );
  return data;
};

export const getExclusionTypes = async (): Promise<ExclusionTypesAPIResponse> => {
  const { data } = await axiosInstance.get(`${userManagementEndpoints.exclusions}`);
  return data;
};

/** GET /user-management/users/:userId - fetch a single user by id (for edit). */
export const getUserById = async (
  userId: string,
  signal?: AbortSignal,
  config?: CustomAxiosRequestConfig,
): Promise<GetUserByIdResponse> => {
  const { data } = await axiosInstance.get<GetUserByIdResponse>(
    `${userManagementEndpoints.users}/${userId}`,
    { ...config, signal },
  );
  return data;
};

export const getExclusionOptions = async ({
  exclusionType,
  page = 1,
  pageSize = 99,
  signal,
}: GetExclusionOptionsParams): Promise<UserManagementAPIResponse> => {
  const queryParams: Record<string, string> = {};
  if (page != null) {
    queryParams.page = page.toString();
  }
  if (pageSize != null) {
    queryParams.pageSize = pageSize.toString();
  }
  const paginationQuery = getQueryParamsWithCustomQueryObj(queryParams);
  const { data } = await axiosInstance.get(
    `${userManagementEndpoints.exclusionValues}${exclusionType}&${paginationQuery}`,
    { signal },
  );
  return data;
};

/** The users list's Role filter at ServEngine level, where the rows span every entity. */
export const ROLES_ACROSS_ENTITIES_FILTER_KEY = "roleAcrossEntities";

export const getUserDropdown = async (
  _type: string,
  search?: string,
  signal?: AbortSignal,
  key?: string,
): Promise<any> => {
  const params = new URLSearchParams();

  if (search) {
    params.append("search", search);
  }

  const userManagementDropdownKeys = new Set([
    "user",
    "role",
    ROLES_ACROSS_ENTITIES_FILTER_KEY,
    "reportsTo",
    "assignedTo",
    "email",
    "phoneNumber",
  ]);

  if (key === "user") {
    params.append("type", "user");
    params.append("field", "label");
    params.append("field", "value");
  }

  if (key === "reportsTo") {
    params.append("type", "reportsTo");
  }

  if (key === "role" || key === ROLES_ACROSS_ENTITIES_FILTER_KEY) {
    params.append("type", "roles");
    params.append("field", "label");
    params.append("field", "value");
    // SERV-3574 (D264): the names the Roles column can show, the platform copies included. Opt-in,
    // because the role pickers read the same type for ids their save can take.
    params.append("forUserList", "true");
  }

  // SERV-3573 (D264): the names held in any entity; the backend answers it only at ServEngine level.
  if (key === ROLES_ACROSS_ENTITIES_FILTER_KEY) {
    params.append("acrossEntities", "true");
  }

  if (key === "assignedTo") {
    params.append("type", "assignedTo");
  }

  if (key === "email") {
    params.append("type", "email");
  }

  if (key === "phoneNumber") {
    params.append("type", "phoneNumber");
  }

  const finalQueryString = getQueryParamsWithCustomQueryObj(params);

  const baseUrl = userManagementDropdownKeys.has(key ?? "")
    ? userManagementEndpoints.userManagementDropdown
    : userManagementEndpoints.userDropdown;

  const { data } = await axiosInstance.get<any>(`${baseUrl}?${finalQueryString}`, {
    signal,
  });

  if (userManagementDropdownKeys.has(key ?? "")) {
    return data;
  }

  const typed = data as UsersDropdownResponse;
  return typed?.data ?? [];
};

/**
 * Every user in the system, across every entity.
 *
 * Backs the chat contact picker, where an app-level super admin needs to reach
 * people belonging to entities the admin is not a member of. `/user-management/users`
 * cannot serve this: it is scoped to the caller's tenant, which at app level is
 * the default entity, so it returns super admins and nothing else.
 *
 * Returns 403 for anything but an app-level super admin.
 */
export const getUserDirectory = async ({
  search,
  page = 1,
  pageSize = 500,
  entityId,
  signal,
}: {
  search?: string;
  page?: number;
  pageSize?: number;
  entityId?: string;
  signal?: AbortSignal;
} = {}): Promise<UserDirectoryResponse> => {
  const params = new URLSearchParams();
  params.append("page", String(page));
  params.append("pageSize", String(pageSize));
  if (search?.trim()) {
    params.append("search", search.trim());
  }
  if (entityId) {
    params.append("entityId", entityId);
  }

  const response = await axiosInstance.get<UserDirectoryResponse>(
    `${userManagementEndpoints.userDirectory}?${params.toString()}`,
    { signal },
  );
  return response.data;
};

export type UserPickerOption = { id: string; label: string; value: string };

/**
 * Users of the caller's entity, for pickers that name people rather than roles.
 * Scoped by the backend to the caller's entity; the AP notify pickers rely on
 * that, since a recipient outside it is rejected on save.
 */
export const getUserPickerOptions = async (
  search?: string,
  signal?: AbortSignal,
  entityIds?: string[],
  actingEntityId?: string,
): Promise<UserPickerOption[]> => {
  const { data } = await axiosInstance.get<{ data: UserPickerOption[] }>(
    userManagementEndpoints.userManagementDropdown,
    {
      params: {
        type: "user",
        pageSize: 100,
        ...(search ? { search } : {}),
        // Which entities' users to list. The backend pins an entity login to
        // its own tenant and honours this only for an app-level caller, whose
        // own tenant is the default entity rather than the one it is acting
        // for. Plural because an account shared into other entities is payable
        // by them, so their users are nameable too. Omitted, unchanged.
        ...(entityIds?.length ? { entityIds: entityIds.join(",") } : {}),
      },
      signal,
      actingEntityId,
    } as CustomAxiosRequestConfig,
  );
  return data?.data ?? [];
};
